import { FormControl } from '@mui/material';
import { FC, useMemo } from 'react';
import { useQuery } from 'react-query';
import { alphaSort } from '../../helpers';
import { IDropdownResponse } from '../../models';
import { getSalesLookupNeighborhoods } from '../../fetch/lookups/sales-portal';
import { AutoCompleteMultiple } from '../input/AutoCompleteMultiple';

interface ISalesNeighborhoodAutoCompleteProps {
  value: string | number | string[] | number[];
  handleChange: (val: string | number | string[] | number[]) => void; // Accepts single or multiple values
  isDisabled?: boolean;
  setOptions?: (val: IDropdownResponse[]) => void;
  apiParams?: any;
  isRequired?: boolean;
  inputLabel?: string;
  isEnabled?: boolean;
  name?: string;
  handleClear?: () => void;
  limitTags?: number;
}

export const SalesNeighborhoodAutoComplete: FC<ISalesNeighborhoodAutoCompleteProps> = ({
  inputLabel,
  isDisabled,
  isRequired,
  value,
  handleChange,
  setOptions,
  apiParams,
  name,
  isEnabled = true,
  handleClear,
  limitTags
}) => {
  const { isLoading: isLoadingNeighborhoods, data: neighborhoodsData = [] } = useQuery<
    IDropdownResponse[],
    Error
  >(['neighborhoods-filters', apiParams], () => getSalesLookupNeighborhoods(apiParams), {
    notifyOnChangeProps: 'tracked',
    onSuccess: data => setOptions?.(data),
    enabled: isEnabled
  });

  const neighborhoods = useMemo(() => {
    if (!Array.isArray(neighborhoodsData)) {
      return [];
    }
    return neighborhoodsData.map(r => ({
      ...r,
      fullName: `${r.description}`
    }));
  }, [neighborhoodsData]);

  const options = alphaSort(neighborhoods, 'description').map(neighborhood => ({
    value: neighborhood?.value,
    label: neighborhood?.description
  }));

  const flattenedValue = useMemo(() => {
    if (Array.isArray(value)) {
      return value.flat();
    }
    return [value];
  }, [value]);

  const selectedOptions = useMemo(() => {
    if (Array.isArray(flattenedValue)) {
      return options.filter(option =>
        (flattenedValue as (string | number)[]).includes(option.value)
      );
    }
    const selectedOption = options.find(option => option.value === flattenedValue);
    return selectedOption ? [selectedOption] : [];
  }, [flattenedValue, options]);

  // Filter options to exclude selected neighborhoods from dropdown when active
  const filteredOptions = useMemo(() => {
    return options.filter(option => !flattenedValue.includes(option.value));
  }, [options, flattenedValue]);

  return (
    <FormControl fullWidth variant="outlined" size="small" required={isRequired}>
      <AutoCompleteMultiple
        limitTags={limitTags}
        name={name || null}
        options={filteredOptions}
        label={isLoadingNeighborhoods ? 'Loading...' : inputLabel ?? 'Neighborhoods'}
        value={selectedOptions}
        required={isRequired}
        disabled={isDisabled || isLoadingNeighborhoods}
        disableCloseOnSelect={true}
        onChange={(event, newValue) => {
          if (!newValue || (Array.isArray(newValue) && newValue.length === 0)) {
            handleClear && handleClear();
            handleChange([]);
          } else if (Array.isArray(newValue)) {
            handleChange(newValue.map(option => option.value));
          }
        }}
      />
    </FormControl>
  );
};
